import { useEffect, useRef } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { OpenAPI } from "client/openapi";
import { PublicMessage } from "components/PublicBooking/PublicShell";
import {
  EMBED_HEIGHT_MESSAGE,
  EMBED_HELLO_MESSAGE,
  EMBED_PATH,
  EMBED_TOP_ID,
  revealTop,
} from "util/embed";
import OrganizationWebPage from "./OrganizationWebPage";
import TutorWebPage from "./TutorWebPage";

/**
 * The app for /embed/...: the booking pages as widgets on a business's own
 * website (the Tutors and Classes tabs' content, and a tutor's booking
 * flow), with every flow inside the frame. It runs without the login (no
 * Auth0), so a visitor signed in to Tadpole never makes a sign-in call from
 * someone else's website.
 *
 * It tells the website how tall it is, so the embed code sizes the frame
 * with no scrolling inside it, and lets the website show around the card.
 * It says so again when the embed code says hello, as a script a speed
 * plugin held back attaches after the first posts. On a move to another
 * page, the widget's top comes into view; nothing scrolls when it first
 * loads.
 */
export default function BookingEmbedApp() {
  OpenAPI.BASE = process.env.REACT_APP_BACKEND_API_DOMAIN || "";
  const box = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const element = box.current;
    if (!element) return;
    document.body.style.background = "transparent";
    document.body.style.overflowY = "auto";
    const post = () =>
      window.parent.postMessage(
        {
          type: EMBED_HEIGHT_MESSAGE,
          height: Math.ceil(element.getBoundingClientRect().height),
        },
        "*"
      );
    const observer = new ResizeObserver(post);
    observer.observe(element);
    post();
    const hello = (e: MessageEvent) => {
      if (e.source === window.parent && e.data?.type === EMBED_HELLO_MESSAGE)
        post();
    };
    window.addEventListener("message", hello);
    return () => {
      observer.disconnect();
      window.removeEventListener("message", hello);
    };
  }, []);

  const shown = useRef(pathname);
  useEffect(() => {
    if (shown.current === pathname) return;
    shown.current = pathname;
    revealTop();
  }, [pathname]);

  return (
    <div ref={box}>
      <div id={EMBED_TOP_ID} />
      <Routes>
        <Route
          path={`${EMBED_PATH}/organizations/:id`}
          element={<OrganizationWebPage />}
        />
        <Route
          path={`${EMBED_PATH}/organizations/:id/classes`}
          element={<OrganizationWebPage tab="classes" />}
        />
        <Route path={`${EMBED_PATH}/tutors/:id`} element={<TutorWebPage />} />
        <Route
          path="*"
          element={<PublicMessage title="This page isn't available." />}
        />
      </Routes>
    </div>
  );
}
